import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const STATUS_BADGE = {
  paye: { label: 'Payé', cls: 'bg-green-100 text-green-700', dot: 'bg-green-500' },
  attente: { label: 'En attente', cls: 'bg-yellow-100 text-yellow-700', dot: 'bg-yellow-500' },
  impaye: { label: 'Impayé', cls: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
};

export const eur = (x) => `${Number(x || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const EMPTY_FORM = {
  owner_lead_id: '', owner_name: '', owner_company: '', owner_email: '', owner_phone: '',
  owner_iban: '', owner_bic: '', owner_address: '',
  address: '', lot_label: '', property_type: '', surface_m2: '', irl_ref: '', zone_tendue: false,
  rent_amount: '', charges_amount: '', charges_detail: '',
  payment_mode: 'virement', mgmt_pct: '6', mgmt_min: '50', pct_mode: 'ht', start_date: '', payment_day: '5', branch: '',
  no_management: false,
  gli_enabled: false, gli_rate_pct: '2.5',
  notice_enabled: true, notice_days_before: '3',
  tenant_first_name: '', tenant_last_name: '', tenant_email: '', tenant_phone: '',
  tenant_entry_date: '', tenant_lease_date: '', tenant_rent: '', tenant_deposit: '',
};

const PropertyFormModal = ({ onClose, onCreated, user }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [leads, setLeads] = useState([]);
  const [showTenant, setShowTenant] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    axios.get(`${API}/crm/leads`).then(r => setLeads(r.data || [])).catch(() => {});
  }, []);

  const selectLead = (leadId) => {
    set('owner_lead_id', leadId);
    const l = leads.find(x => x.lead_id === leadId);
    if (l) {
      setForm(f => ({
        ...f, owner_lead_id: leadId,
        owner_name: `${l.first_name || ''} ${l.last_name || ''}`.trim(),
        owner_email: l.email || '', owner_phone: l.phone || '',
      }));
    }
  };

  const submit = async () => {
    if (!form.owner_name.trim() || !form.address.trim() || !form.rent_amount) {
      toast.error('Propriétaire, adresse et loyer sont obligatoires.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        owner_lead_id: form.owner_lead_id || null,
        owner_name: form.owner_name, owner_company: form.owner_company,
        owner_email: form.owner_email, owner_phone: form.owner_phone,
        owner_iban: form.owner_iban, owner_bic: form.owner_bic, owner_address: form.owner_address,
        address: form.address, lot_label: form.lot_label, property_type: form.property_type,
        surface_m2: form.surface_m2 ? parseFloat(form.surface_m2) : null,
        irl_ref: form.irl_ref, zone_tendue: !!form.zone_tendue,
        rent_amount: parseFloat(form.rent_amount),
        charges_amount: parseFloat(form.charges_amount || 0),
        charges_detail: form.charges_detail,
        payment_mode: form.payment_mode,
        no_management: !!form.no_management,
        mgmt_pct: parseFloat(form.mgmt_pct || 0),
        mgmt_min: parseFloat(form.mgmt_min || 0),
        pct_mode: form.pct_mode || 'ht',
        gli_enabled: !!form.gli_enabled,
        gli_rate_pct: parseFloat(form.gli_rate_pct || 0),
        start_date: form.start_date, payment_day: parseInt(form.payment_day || 5, 10),
        notice_enabled: !!form.notice_enabled,
        notice_days_before: parseInt(form.notice_days_before || 3, 10),
        branch: form.branch || null,
        tenant: {
          first_name: form.tenant_first_name, last_name: form.tenant_last_name,
          email: form.tenant_email, phone: form.tenant_phone,
          entry_date: form.tenant_entry_date, lease_date: form.tenant_lease_date,
          guarantor_name: form.tenant_guarantor_name || '', guarantor_address: form.tenant_guarantor_address || '',
          rent_amount: form.tenant_rent ? parseFloat(form.tenant_rent) : null,
          deposit: form.tenant_deposit ? parseFloat(form.tenant_deposit) : null,
        },
      };
      const r = await axios.post(`${API}/gestion/properties`, payload);
      toast.success('Bien créé avec succès');
      onCreated(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la création');
    } finally { setSaving(false); }
  };

  const input = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#008A8D] focus:border-[#008A8D]';
  const label = 'block text-xs font-semibold text-gray-600 mb-1';

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="property-form-modal">
        <h2 className="text-lg font-bold text-gray-900 mb-4">Nouveau bien en gestion</h2>

        <h3 className="text-sm font-bold text-[#008A8D] mb-2">Propriétaire</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <div className="sm:col-span-2">
            <label className={label}>Client CRM (optionnel)</label>
            <select value={form.owner_lead_id} onChange={e => selectLead(e.target.value)} className={input} data-testid="owner-lead-select">
              <option value="">— Saisie manuelle —</option>
              {leads.map(l => <option key={l.lead_id} value={l.lead_id}>{l.first_name} {l.last_name} {l.email ? `(${l.email})` : ''}</option>)}
            </select>
          </div>
          <div><label className={label}>Nom / Prénom *</label><input className={input} value={form.owner_name} onChange={e => set('owner_name', e.target.value)} data-testid="owner-name-input" /></div>
          <div><label className={label}>Société</label><input className={input} value={form.owner_company} onChange={e => set('owner_company', e.target.value)} data-testid="owner-company-input" /></div>
          <div><label className={label}>Email</label><input className={input} type="email" value={form.owner_email} onChange={e => set('owner_email', e.target.value)} data-testid="owner-email-input" /></div>
          <div><label className={label}>Téléphone</label><input className={input} value={form.owner_phone} onChange={e => set('owner_phone', e.target.value)} /></div>
          <div><label className={label}>IBAN (virements propriétaire)</label><input className={input} value={form.owner_iban} onChange={e => set('owner_iban', e.target.value)} placeholder="FR76..." data-testid="owner-iban-input" /></div>
          <div><label className={label}>BIC</label><input className={input} value={form.owner_bic} onChange={e => set('owner_bic', e.target.value)} placeholder="AGRIFRPP..." /></div>
          <div className="sm:col-span-2"><label className={label}>Adresse postale du propriétaire (sur le compte rendu)</label><input className={input} value={form.owner_address} onChange={e => set('owner_address', e.target.value)} placeholder="2 rue Angéla Duval, 56620 CLEGUER" data-testid="owner-address-input" /></div>
        </div>

        <h3 className="text-sm font-bold text-[#008A8D] mb-2">Bien & conditions de gestion</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <div className="sm:col-span-2"><label className={label}>Adresse du bien *</label><input className={input} value={form.address} onChange={e => set('address', e.target.value)} data-testid="property-address-input" /></div>
          <div><label className={label}>Lot (n° / référence)</label><input className={input} value={form.lot_label} onChange={e => set('lot_label', e.target.value)} placeholder="Lot 60276" data-testid="lot-label-input" /></div>
          <div><label className={label}>Type de bien</label><input className={input} value={form.property_type} onChange={e => set('property_type', e.target.value)} placeholder="Maison 3 pièces, Appartement T2..." data-testid="property-type-input" /></div>
          <div><label className={label}>Surface habitable (m²)</label><input className={input} type="number" step="0.01" value={form.surface_m2} onChange={e => set('surface_m2', e.target.value)} data-testid="surface-input" /></div>
          <div><label className={label}>IRL — trimestre de référence</label><input className={input} value={form.irl_ref} onChange={e => set('irl_ref', e.target.value)} placeholder="ex : 2e trimestre 2026" data-testid="irl-ref-input" /></div>
          <div className="flex items-end pb-2"><label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={form.zone_tendue} onChange={e => set('zone_tendue', e.target.checked)} className="accent-[#008A8D]" data-testid="zone-tendue-checkbox" />
            Zone tendue (encadrement des loyers)
          </label></div>
          <div><label className={label}>Loyer mensuel CC (€) *</label><input className={input} type="number" value={form.rent_amount} onChange={e => set('rent_amount', e.target.value)} data-testid="rent-amount-input" /></div>
          <div><label className={label}>Dont charges (€)</label><input className={input} type="number" value={form.charges_amount} onChange={e => set('charges_amount', e.target.value)} /></div>
          <div className="sm:col-span-2"><label className={label}>Détail des charges</label><input className={input} value={form.charges_detail} onChange={e => set('charges_detail', e.target.value)} placeholder="Ex: eau, ordures ménagères, entretien communs..." /></div>
          <div>
            <label className={label}>Mode de paiement</label>
            <select className={input} value={form.payment_mode} onChange={e => set('payment_mode', e.target.value)} data-testid="payment-mode-select">
              <option value="virement">Virement</option>
              <option value="prelevement">Prélèvement</option>
            </select>
          </div>
          <div><label className={label}>Jour d'échéance du loyer</label><input className={input} type="number" min="1" max="28" value={form.payment_day} onChange={e => set('payment_day', e.target.value)} /></div>
          <div className="sm:col-span-2">
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              <input type="checkbox" checked={form.no_management} onChange={e => set('no_management', e.target.checked)} className="accent-[#008A8D]" data-testid="no-management-checkbox" />
              <span><b>Bien sans gestion</b> (mes propres locataires) — quittances automatiques uniquement, 0 € d'honoraires, pas de compte rendu</span>
            </label>
          </div>
          <div><label className={label}>% de gestion</label>
            <div className="flex gap-2">
              <input className={input} type="number" step="0.1" value={form.mgmt_pct} onChange={e => set('mgmt_pct', e.target.value)} disabled={form.no_management} data-testid="mgmt-pct-input" />
              <select className={input + ' w-24'} value={form.pct_mode} onChange={e => set('pct_mode', e.target.value)} disabled={form.no_management} data-testid="pct-mode-select">
                <option value="ht">% HT</option>
                <option value="ttc">% TTC</option>
              </select>
            </div>
          </div>
          <div><label className={label}>Minimum mensuel TTC (€)</label><input className={input} type="number" value={form.mgmt_min} onChange={e => set('mgmt_min', e.target.value)} disabled={form.no_management} data-testid="mgmt-min-input" /></div>
          <div className="sm:col-span-2">
            <label className={label}>Garantie Loyers Impayés (GLI)</label>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={form.gli_enabled} onChange={e => set('gli_enabled', e.target.checked)} className="accent-[#008A8D]" data-testid="gli-enabled-checkbox" />
              <span className="text-xs text-gray-600">Souscrite — taux</span>
              <input className={input + ' w-20'} type="number" step="0.1" value={form.gli_rate_pct} onChange={e => set('gli_rate_pct', e.target.value)} disabled={!form.gli_enabled} data-testid="gli-rate-input" />
              <span className="text-xs text-gray-500">% ({form.pct_mode === 'ttc' ? 'TTC' : 'HT'}, même mode que la gestion) du loyer — facturée séparément sur le compte rendu</span>
            </div>
          </div>
          <div><label className={label}>Date de début de gestion</label><input className={input} type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)} data-testid="start-date-input" /></div>
          <div><label className={label}>Avis d'échéance locataire</label>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={form.notice_enabled} onChange={e => set('notice_enabled', e.target.checked)} className="accent-[#008A8D]" data-testid="notice-enabled-checkbox" />
              <input className={input + ' w-16'} type="number" min="1" max="15" value={form.notice_days_before} onChange={e => set('notice_days_before', e.target.value)} data-testid="notice-days-input" />
              <span className="text-xs text-gray-500">jours avant</span>
            </div>
          </div>
          {user?.cross_branch_access && (
            <div>
              <label className={label}>Branche</label>
              <select className={input} value={form.branch} onChange={e => set('branch', e.target.value)} data-testid="branch-select">
                <option value="">Ma branche par défaut</option>
                <option value="bretagne">Bretagne</option>
                <option value="nord">Nord</option>
              </select>
            </div>
          )}
        </div>

        <button onClick={() => setShowTenant(s => !s)} className="text-sm font-semibold text-[#008A8D] mb-3" data-testid="toggle-tenant-btn">
          {showTenant ? '− Masquer le locataire' : '+ Ajouter le locataire maintenant'}
        </button>
        {showTenant && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4 bg-gray-50 p-4 rounded-lg">
            <div><label className={label}>Prénom</label><input className={input} value={form.tenant_first_name} onChange={e => set('tenant_first_name', e.target.value)} data-testid="tenant-first-name-input" /></div>
            <div><label className={label}>Nom</label><input className={input} value={form.tenant_last_name} onChange={e => set('tenant_last_name', e.target.value)} data-testid="tenant-last-name-input" /></div>
            <div><label className={label}>Email</label><input className={input} type="email" value={form.tenant_email} onChange={e => set('tenant_email', e.target.value)} data-testid="tenant-email-input" /></div>
            <div><label className={label}>Téléphone</label><input className={input} value={form.tenant_phone} onChange={e => set('tenant_phone', e.target.value)} /></div>
            <div><label className={label}>Date d'entrée</label><input className={input} type="date" value={form.tenant_entry_date} onChange={e => set('tenant_entry_date', e.target.value)} /></div>
            <div><label className={label}>Date de signature du bail</label><input className={input} type="date" value={form.tenant_lease_date} onChange={e => set('tenant_lease_date', e.target.value)} data-testid="tenant-lease-date-input" /></div>
            <div><label className={label}>Loyer du bail (€)</label><input className={input} type="number" value={form.tenant_rent} onChange={e => set('tenant_rent', e.target.value)} /></div>
            <div><label className={label}>Dépôt de garantie (€)</label><input className={input} type="number" value={form.tenant_deposit} onChange={e => set('tenant_deposit', e.target.value)} /></div>
            <div><label className={label}>Garant / caution (nom)</label><input className={input} value={form.tenant_guarantor_name || ''} onChange={e => set('tenant_guarantor_name', e.target.value)} data-testid="guarantor-name-input" /></div>
            <div><label className={label}>Adresse du garant</label><input className={input} value={form.tenant_guarantor_address || ''} onChange={e => set('tenant_guarantor_address', e.target.value)} data-testid="guarantor-address-input" /></div>
          </div>
        )}

        <div className="flex justify-end gap-3 mt-4">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800" data-testid="cancel-property-btn">Annuler</button>
          <button onClick={submit} disabled={saving} className="px-6 py-2 bg-[#008A8D] text-white text-sm font-semibold rounded-lg hover:bg-[#006B6D] disabled:opacity-50" data-testid="submit-property-btn">
            {saving ? 'Création...' : 'Créer le bien'}
          </button>
        </div>
      </div>
    </div>
  );
};

const ExportModal = ({ onClose }) => {
  const now = new Date();
  const [period, setPeriod] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const [format, setFormat] = useState('csv');
  const [debtor, setDebtor] = useState({ debtor_name: '', debtor_iban: '', debtor_bic: '' });
  const [busy, setBusy] = useState(false);
  useEffect(() => { axios.get(`${API}/gestion/settings`).then(r => setDebtor(r.data)).catch(() => {}); }, []);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/gestion/exports/owner-transfers`, { period, format, ...debtor });
      const bin = atob(r.data.content_base64);
      const arr = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([arr], { type: r.data.mime }));
      const a = document.createElement('a');
      a.href = url; a.download = r.data.filename; a.click();
      URL.revokeObjectURL(url);
      if (r.data.skipped?.length) toast.warning(`IBAN manquant pour : ${r.data.skipped.join(', ')}`);
      toast.success(`Export généré — ${r.data.count} virement(s), total ${eur(r.data.total)}`);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur lors de l'export"); }
    finally { setBusy(false); }
  };

  const input = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#008A8D] focus:border-[#008A8D]';
  const label = 'block text-xs font-semibold text-gray-600 mb-1';
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()} data-testid="export-transfers-modal">
        <h2 className="text-lg font-bold text-gray-900 mb-1">Export virements propriétaires</h2>
        <p className="text-xs text-gray-500 mb-4">Génère le fichier des virements nets à reverser aux propriétaires pour la période choisie.</p>
        <div className="space-y-3">
          <div><label className={label}>Période</label><input type="month" className={input} value={period} onChange={e => setPeriod(e.target.value)} data-testid="export-period-input" /></div>
          <div>
            <label className={label}>Format</label>
            <div className="flex gap-4 text-sm">
              <label className="flex items-center gap-2 cursor-pointer"><input type="radio" checked={format === 'csv'} onChange={() => setFormat('csv')} className="accent-[#008A8D]" data-testid="export-format-csv" /> CSV (récapitulatif)</label>
              <label className="flex items-center gap-2 cursor-pointer"><input type="radio" checked={format === 'sepa'} onChange={() => setFormat('sepa')} className="accent-[#008A8D]" data-testid="export-format-sepa" /> SEPA XML (import banque)</label>
            </div>
          </div>
          {format === 'sepa' && (
            <div className="bg-gray-50 rounded-lg p-3 space-y-2" data-testid="sepa-debtor-block">
              <p className="text-xs font-semibold text-gray-700">Compte émetteur (votre banque pro)</p>
              <div><label className={label}>Nom du titulaire *</label><input className={input} value={debtor.debtor_name} onChange={e => setDebtor(d => ({ ...d, debtor_name: e.target.value }))} placeholder="RealEase SAS" data-testid="sepa-debtor-name" /></div>
              <div><label className={label}>IBAN *</label><input className={input} value={debtor.debtor_iban} onChange={e => setDebtor(d => ({ ...d, debtor_iban: e.target.value }))} placeholder="FR76..." data-testid="sepa-debtor-iban" /></div>
              <div><label className={label}>BIC</label><input className={input} value={debtor.debtor_bic} onChange={e => setDebtor(d => ({ ...d, debtor_bic: e.target.value }))} placeholder="AGRIFRPP..." data-testid="sepa-debtor-bic" /></div>
              <p className="text-[10px] text-gray-400">Ces informations sont mémorisées pour les prochains exports. Fichier pain.001 importable dans la plupart des espaces bancaires pro.</p>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-3 mt-5">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600">Annuler</button>
          <button onClick={submit} disabled={busy} className="px-6 py-2 bg-[#008A8D] text-white text-sm font-semibold rounded-lg hover:bg-[#006B6D] disabled:opacity-50" data-testid="confirm-export-btn">
            {busy ? 'Génération...' : 'Télécharger'}
          </button>
        </div>
      </div>
    </div>
  );
};

const PortfolioView = ({ navigate }) => {
  const [data, setData] = useState(null);
  useEffect(() => { axios.get(`${API}/gestion/portfolio`).then(r => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <div className="p-8 text-gray-400">Chargement...</div>;
  if (!data.owners?.length) return <div className="realease-card p-12 text-center text-gray-400">Aucun bien en gestion.</div>;
  return (
    <div className="space-y-4" data-testid="portfolio-view">
      {data.owners.map((o, i) => (
        <div key={i} className="realease-card p-5" data-testid={`portfolio-owner-${i}`}>
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div>
              <h3 className="font-bold text-gray-900">{o.owner_name}{o.owner_company ? ` (${o.owner_company})` : ''}</h3>
              <p className="text-xs text-gray-500">{o.owner_email || '—'} {!o.has_iban && <span className="text-orange-500 font-medium">· IBAN manquant</span>}</p>
            </div>
            <div className="flex gap-6 text-sm text-right items-center">
              <div><p className="text-xs text-gray-500">Biens</p><b>{o.properties.length}</b></div>
              <div><p className="text-xs text-gray-500">Loyers / mois</p><b>{eur(o.total_rent_monthly)}</b></div>
              <div><p className="text-xs text-gray-500">Net perçu (année)</p><b className="text-[#008A8D]">{eur(o.total_net_year)}</b></div>
              <a href={`${API}/gestion/properties/${o.properties[0].property_id}/annual-report?year=${new Date().getFullYear()}`} target="_blank" rel="noreferrer"
                className="px-3 py-1.5 text-xs font-semibold border border-[#008A8D] text-[#008A8D] rounded-lg hover:bg-[#008A8D]/5" data-testid={`annual-report-owner-${i}`}>
                Relevé annuel
              </a>
            </div>
          </div>
          <div className="space-y-1.5">
            {o.properties.map(p => {
              const st = STATUS_BADGE[p.status] || STATUS_BADGE.attente;
              return (
                <div key={p.property_id} onClick={() => navigate(`/gestion/${p.property_id}`)}
                  className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer text-sm">
                  <span className="font-medium">{p.address}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-gray-500">{eur(p.rent_amount)}</span>
                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${st.cls}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`}></span>{st.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default function GestionLocativePage() {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [view, setView] = useState('biens');
  const navigate = useNavigate();

  const fetchAll = useCallback(async () => {
    try {
      const [d, p] = await Promise.all([
        axios.get(`${API}/gestion/dashboard`),
        axios.get(`${API}/gestion/properties`),
      ]);
      setDashboard(d.data); setProperties(p.data);
    } catch (e) { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading) return <div className="p-8 text-gray-400">Chargement...</div>;

  const stats = [
    { label: 'Biens gérés', value: dashboard?.properties_count ?? 0, testid: 'stat-properties' },
    { label: 'Loyers perçus (mois)', value: eur(dashboard?.rents_received_month), testid: 'stat-rents' },
    { label: 'Honoraires facturés (mois)', value: eur(dashboard?.fees_month), testid: 'stat-fees' },
    { label: 'Honoraires facturés (année)', value: eur(dashboard?.fees_year), testid: 'stat-fees-year' },
    { label: 'Impayés en cours', value: dashboard?.impayes_count ?? 0, testid: 'stat-impayes', alert: (dashboard?.impayes_count || 0) > 0 },
  ];

  return (
    <div className="p-6 lg:p-8" data-testid="gestion-locative-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion Locative</h1>
          <p className="text-sm text-gray-500">Portefeuille de biens en gestion — {dashboard?.period}</p>
        </div>
        <div className="flex gap-2">
          <a href={`${API}/gestion/gli-bordereau`} target="_blank" rel="noreferrer"
             className="px-4 py-2.5 border border-amber-500 text-amber-700 text-sm font-semibold rounded-lg hover:bg-amber-50"
             title="Bordereau mensuel des biens en gestion avec Garantie Loyers Impayes (pour le garantisseur)"
             data-testid="gli-bordereau-btn">
            📄 Bordereau GLI
          </a>
          <button onClick={() => setShowExport(true)} className="px-4 py-2.5 border border-[#008A8D] text-[#008A8D] text-sm font-semibold rounded-lg hover:bg-[#008A8D]/5" data-testid="export-transfers-btn">
            Export virements
          </button>
          <button onClick={() => setShowForm(true)} className="px-5 py-2.5 bg-[#008A8D] text-white text-sm font-semibold rounded-lg hover:bg-[#006B6D]" data-testid="new-property-btn">
            + Nouveau bien
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        {stats.map(s => (
          <div key={s.label} className="realease-card p-5" data-testid={s.testid}>
            <p className="text-xs text-gray-500 mb-1">{s.label}</p>
            <p className={`text-2xl font-bold ${s.alert ? 'text-red-600' : 'text-gray-900'}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {(dashboard?.upcoming_revisions || []).length > 0 && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid="revision-alerts-banner">
          <p className="text-sm font-bold text-amber-900 mb-2">📈 Révisions de loyer (IRL) à prévoir — anniversaire de bail dans les 60 jours</p>
          <div className="space-y-1.5">
            {dashboard.upcoming_revisions.map(r => (
              <div key={r.property_id} className="flex items-center justify-between flex-wrap gap-2 text-sm" data-testid={`revision-alert-${r.property_id}`}>
                <span className="text-amber-900">
                  <b>{r.address}</b>{r.tenant ? ` — ${r.tenant}` : ''} · anniversaire le {r.anniversary.split('-').reverse().join('/')}
                  {r.days_left <= 15 ? <b className="text-red-600"> ({r.days_left} j)</b> : ` (${r.days_left} j)`}
                </span>
                <button onClick={() => navigate(`/gestion/${r.property_id}`)} className="text-xs font-semibold text-[#008A8D] hover:underline" data-testid={`revision-alert-link-${r.property_id}`}>
                  Réviser le loyer →
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-1 mb-4" data-testid="gestion-view-toggle">
        <button onClick={() => setView('biens')} className={`px-4 py-1.5 rounded-full text-sm font-semibold ${view === 'biens' ? 'bg-[#008A8D] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-testid="view-biens-btn">Par bien</button>
        <button onClick={() => setView('proprietaires')} className={`px-4 py-1.5 rounded-full text-sm font-semibold ${view === 'proprietaires' ? 'bg-[#008A8D] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-testid="view-owners-btn">Par propriétaire</button>
      </div>

      {view === 'proprietaires' ? <PortfolioView navigate={navigate} /> : (
      <div className="realease-card overflow-hidden">
        {properties.length === 0 ? (
          <div className="p-12 text-center text-gray-400" data-testid="no-properties">
            Aucun bien en gestion. Cliquez sur "+ Nouveau bien" pour commencer.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase">
                <th className="px-4 py-3">Bien</th>
                <th className="px-4 py-3">Propriétaire</th>
                <th className="px-4 py-3">Locataire</th>
                <th className="px-4 py-3 text-right">Loyer CC</th>
                <th className="px-4 py-3 text-right">Gestion</th>
                <th className="px-4 py-3">Statut du mois</th>
              </tr>
            </thead>
            <tbody>
              {properties.map(p => {
                const st = STATUS_BADGE[dashboard?.statuses?.[p.property_id]] || STATUS_BADGE.attente;
                const tenant = `${p.tenant?.first_name || ''} ${p.tenant?.last_name || ''}`.trim();
                return (
                  <tr key={p.property_id} onClick={() => navigate(`/gestion/${p.property_id}`)}
                    className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer" data-testid={`property-row-${p.property_id}`}>
                    <td className="px-4 py-3 font-medium text-gray-900">{p.address}
                      {p.branch && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 uppercase">{p.branch}</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{p.owner_name}{p.owner_company ? ` (${p.owner_company})` : ''}</td>
                    <td className="px-4 py-3 text-gray-600">{tenant || <span className="text-gray-300">—</span>}</td>
                    <td className="px-4 py-3 text-right font-medium">{eur(p.rent_amount)}</td>
                    <td className="px-4 py-3 text-right text-gray-600">{p.no_management ? 'Sans gestion' : `${p.mgmt_pct}% ${p.pct_mode === 'ttc' ? 'TTC' : 'HT'} (min ${eur(p.mgmt_min)})`}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${st.cls}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`}></span>{st.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      )}

      {showExport && <ExportModal onClose={() => setShowExport(false)} />}
      {showForm && <PropertyFormModal user={user} onClose={() => setShowForm(false)}
        onCreated={(p) => { setShowForm(false); navigate(`/gestion/${p.property_id}`); }} />}
    </div>
  );
}
